import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Megaphone, Loader2, Send, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './adminConstants';

const AdminQuickLinkBroadcast = () => {
  const { getToken } = useAuth();
  const [preview, setPreview] = useState(null);
  const [resend, setResend] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const pollRef = useRef(null);

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${getToken()}` } }), [getToken]);

  const fetchPreview = useCallback((withResend) => {
    axios.post(`${API}/admin/quick-link-broadcast`, { dry_run: true, resend: withResend }, authHeaders())
      .then(r => setPreview(r.data)).catch(() => {});
  }, [authHeaders]);

  useEffect(() => { fetchPreview(resend); }, [fetchPreview, resend]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const startPolling = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/admin/quick-link-broadcast/${id}`, authHeaders());
        setProgress(r.data);
        if (r.data.status === 'done') {
          clearInterval(pollRef.current);
          setSending(false);
          toast.success(`Broadcast concluído: ${r.data.sent_whatsapp} WhatsApp + ${r.data.sent_email} e-mails`);
          fetchPreview(resend);
        }
      } catch { clearInterval(pollRef.current); setSending(false); }
    }, 3000);
  };

  const fire = async () => {
    const total = (preview?.partners || 0) + (preview?.affiliates || 0);
    if (!window.confirm(`Disparar o Link Relâmpago para ${total} pessoas (${preview?.partners} parceiros + ${preview?.affiliates} afiliados) por WhatsApp e e-mail agora?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/quick-link-broadcast`, { dry_run: false, resend }, authHeaders());
      setProgress({ total: r.data.partners + r.data.affiliates, done: 0, sent_whatsapp: 0, sent_email: 0, status: 'processing' });
      startPolling(r.data.broadcast_id);
      toast.success('Broadcast iniciado! Envios com intervalo anti-spam.');
    } catch (err) {
      setSending(false);
      toast.error(err.response?.data?.detail || 'Erro ao iniciar broadcast');
    }
  };

  const total = (preview?.partners || 0) + (preview?.affiliates || 0);

  return (
    <Card data-testid="broadcast-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Megaphone className="w-5 h-5 text-[#C5A059]" /> Divulgar Link Relâmpago</CardTitle>
        <p className="text-sm text-slate-500">
          Envia a novidade do Link Relâmpago (com o link pessoal de cada um) para todos os parceiros e afiliados por WhatsApp + e-mail. Envio único com trava anti-duplicidade.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!preview ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#C5A059]" /></div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2 text-center" data-testid="broadcast-counts">
              {[['Parceiros', preview.partners], ['Afiliados', preview.affiliates], ['C/ WhatsApp', preview.with_whatsapp], ['C/ E-mail', preview.with_email]].map(([l, v]) => (
                <div key={l} className="bg-slate-50 rounded-lg py-2">
                  <p className="text-xl font-bold text-[#0A2647]">{v}</p>
                  <p className="text-[10px] text-slate-400 uppercase">{l}</p>
                </div>
              ))}
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-500 font-semibold">Ver prévia da mensagem (WhatsApp)</summary>
              <pre className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap font-sans text-slate-600" data-testid="broadcast-preview">{preview.preview_whatsapp}</pre>
            </details>
            <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
              <input type="checkbox" checked={resend} onChange={e => setResend(e.target.checked)} data-testid="broadcast-resend" />
              Reenviar também para quem já recebeu antes
            </label>
            {progress && progress.status === 'processing' && (
              <div className="space-y-1" data-testid="broadcast-progress">
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#C5A059] transition-all" style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }} />
                </div>
                <p className="text-[11px] text-slate-500">{progress.done}/{progress.total} enviados · {progress.sent_whatsapp} WhatsApp · {progress.sent_email} e-mails</p>
              </div>
            )}
            {progress && progress.status === 'done' && (
              <p className="flex items-center gap-1.5 text-xs text-emerald-700 font-semibold" data-testid="broadcast-done">
                <CheckCircle2 className="w-4 h-4" /> Concluído: {progress.sent_whatsapp} WhatsApp · {progress.sent_email} e-mails · {progress.errors || 0} erros
              </p>
            )}
            <Button onClick={fire} disabled={sending || total === 0} className="w-full bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2" data-testid="broadcast-fire-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {total === 0 ? 'Todos já receberam (marque reenviar)' : `Disparar agora para ${total} parceiros/afiliados`}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminQuickLinkBroadcast;
